import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useEligibleProcessors } from "@/services/admin/merchant-processors"
import type { MerchantProcessor } from "@/types/admin-merchant-processor"
import type { ProcessorConfigItem } from "@/types/admin-processor-config"

const prefix = "pages.adminProcessorConfig"

/**
 * Valor do item "a mesma do principal".
 *
 * O Radix Select não aceita `value=""` num item — string vazia é o valor reservado para "nada
 * selecionado". Este sentinela vira `processorId: null` no payload, que é como o backend escreve
 * "espelha o principal"; string em branco ele recusa com 400, justamente para campo não preenchido
 * não virar decisão do operador.
 */
export const MIRROR_PRINCIPAL = "__mirror-principal__"

interface FallbackSelectCellProps {
  item: ProcessorConfigItem
  /** Id do trigger, para o <Label> da linha apontar para ele. */
  triggerId?: string
  disabled?: boolean
  /** `processor` nulo = espelhar quem processa. */
  onSelect: (
    item: ProcessorConfigItem,
    processor: MerchantProcessor | null
  ) => void
}

/**
 * Combo da contingência da linha: para onde a cobrança vai quando a primeira tentativa falha.
 *
 * Componente próprio, como o `ProcessorSelectCell`, porque chama hooks — um por linha, o que
 * dentro do `map` do card seria chamada condicional de hook. A lista de elegíveis só é buscada
 * quando o combo abre.
 *
 * Mostra "a mesma do principal" quando a contingência aponta para quem processa: é o estado da
 * maioria das linhas, e exibir o nome repetido nos dois combos faria parecer que o operador
 * escolheu isso.
 */
export const FallbackSelectCell = ({
  item,
  triggerId,
  disabled,
  onSelect,
}: FallbackSelectCellProps) => {
  const { t } = useTranslation()
  const [wasOpened, setWasOpened] = useState(false)

  const {
    data: processors = [],
    isLoading,
    isError,
    refetch,
  } = useEligibleProcessors(
    item.whiteLabelId,
    item.method,
    wasOpened
  )

  const methodLabel = t(
    `${prefix}.methods.${item.method}`,
    { defaultValue: item.method }
  )

  const mirrorsPrincipal =
    item.fallbackProcessor?.id != null &&
    item.fallbackProcessor.id === item.processor?.id

  // Três estados, não dois. Contingência ausente NÃO é "espelha o principal": a coluna é
  // NOT NULL no backend, então ausência aqui é credencial que ele não resolveu — linha
  // apagada, processador sem strategy. Exibir "a mesma que processa" afirmaria um
  // roteamento que o front não conhece, e ainda tornaria a escolha do espelho um no-op,
  // porque ela já seria o valor atual.
  const unresolved = item.fallbackProcessor == null

  const currentValue = unresolved
    ? ""
    : mirrorsPrincipal
      ? MIRROR_PRINCIPAL
      : (item.fallbackProcessor?.id ?? "")

  const currentLabel = unresolved
    ? t(`${prefix}.fallback.unresolved`)
    : mirrorsPrincipal
      ? t(`${prefix}.fallback.mirrorsPrincipal`)
      : (item.fallbackProcessor?.name ??
        item.fallbackProcessor?.id ??
        t(`${prefix}.fallback.unresolved`))

  const handleChange = (nextValue: string) => {
    if (nextValue === currentValue) return
    if (nextValue === MIRROR_PRINCIPAL) {
      onSelect(item, null)
      return
    }
    const next = processors.find(
      (processor) => processor.id === nextValue
    )
    if (next) onSelect(item, next)
  }

  return (
    <Select
      value={currentValue}
      disabled={disabled}
      onOpenChange={(open) => {
        if (open) setWasOpened(true)
      }}
      onValueChange={handleChange}
    >
      <SelectTrigger
        id={triggerId}
        className="h-8 w-full min-w-0 max-w-[15rem] text-xs"
        aria-label={t(`${prefix}.fallback.aria`, {
          method: methodLabel,
        })}
      >
        <SelectValue placeholder={currentLabel}>
          {currentLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem
          value={MIRROR_PRINCIPAL}
          className="text-xs"
        >
          {t(`${prefix}.fallback.mirrorsPrincipal`)}
        </SelectItem>
        {isLoading && (
          <div role="presentation" className="p-2">
            <Skeleton
              role="status"
              aria-live="polite"
              aria-label={t(`${prefix}.loadingProcessors`)}
              className="h-6 w-40"
            />
          </div>
        )}
        {/* Erro NÃO é vazio: dizer "nenhuma credencial atende" quando a consulta caiu
            faria o operador concluir que precisa cadastrar uma credencial que já existe. */}
        {!isLoading && isError && (
          <div role="presentation" className="p-2">
            <p
              role="status"
              aria-live="polite"
              className="text-xs text-red-700"
            >
              {t(`${prefix}.processorsLoadError`)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2 h-7 w-full text-xs"
              onClick={() => refetch()}
            >
              {t(`${prefix}.retry`)}
            </Button>
          </div>
        )}
        {!isLoading &&
          !isError &&
          processors.length <= 1 && (
            <p
              role="status"
              aria-live="polite"
              className="px-2 py-3 text-xs text-muted-foreground"
            >
              {t(`${prefix}.fallback.noOtherProcessors`)}
            </p>
          )}
        {processors
          .filter(
            // Só filtra quando o principal está resolvido: com `undefined`, a comparação
            // é sempre verdadeira e o filtro vira no-op silencioso.
            (processor) =>
              item.processor?.id == null ||
              processor.id !== item.processor.id
          )
          .map((processor) => (
            <SelectItem
              key={processor.id}
              value={processor.id}
              className="text-xs"
            >
              {processor.name ?? processor.id}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}
